import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

// Extend dayjs with plugins
dayjs.extend(utc);
dayjs.extend(timezone);

// Timezone configuration mapping - centralized configuration to avoid duplicate code
// and make it easy to add/remove timezones
export const TIMEZONE_CONFIG = {
  // US Timezones
  EST: "America/New_York", // UTC-5 (EST) / UTC-4 (EDT)
  CST: "America/Chicago",  // UTC-6 (CST) / UTC-5 (CDT)
  MST: "America/Denver",   // UTC-7 (MST) / UTC-6 (MDT)
  PST: "America/Los_Angeles", // UTC-8 (PST) / UTC-7 (PDT)

  // International Timezones
  UTC: "UTC",             // UTC+0
  GMT: "Europe/London",   // UTC+0 (GMT) / UTC+1 (BST)
  CET: "Europe/Paris",    // UTC+1 (CET) / UTC+2 (CEST)
  JST: "Asia/Tokyo",      // UTC+9
  IST: "Asia/Kolkata",    // UTC+5:30
  AEST: "Australia/Sydney", // UTC+10 (AEST) / UTC+11 (AEDT)
} as const;

// Type for timezone keys
export type TimezoneKey = keyof typeof TIMEZONE_CONFIG;

// Interface for schedule data
export interface ScheduleData {
  date: dayjs.Dayjs | null;
  time: dayjs.Dayjs | null;
  ampm: string;
  tz: string;
}

// Main function to convert schedule to UTC
// This function handles AM/PM conversion and timezone conversion to UTC
// It automatically accounts for daylight saving time using dayjs timezone plugin
export const convertScheduleToUTC = (
  schedule: ScheduleData
): string | undefined => {
  if (!schedule.date || !schedule.time) return undefined;

  // Create a combined date-time in the specified timezone
  let combinedDateTime = schedule.date
    .hour(schedule.time.hour())
    .minute(schedule.time.minute())
    .second(0)
    .millisecond(0);

  // Adjust for AM/PM
  if (schedule.ampm === "PM" && schedule.time.hour() !== 12) {
    combinedDateTime = combinedDateTime.add(12, "hour");
  } else if (schedule.ampm === "AM" && schedule.time.hour() === 12) {
    combinedDateTime = combinedDateTime.subtract(12, "hour");
  }

  // Convert to UTC using dayjs timezone conversion
  // This automatically handles daylight saving time transitions
  const timezoneName = TIMEZONE_CONFIG[schedule.tz as TimezoneKey];

  let utcDateTime;
  if (timezoneName === "UTC") {
    utcDateTime = combinedDateTime.utc();
  } else if (timezoneName) {
    utcDateTime = combinedDateTime.tz(timezoneName).utc();
  } else {
    // Fallback to UTC if timezone not recognized
    utcDateTime = combinedDateTime.utc();
  }

  const utcString = utcDateTime.format("YYYY-MM-DDTHH:mm:ss[Z]");

  return utcString;
};

// Helper function to get all available timezone options for select components
export const getTimezoneOptions = () => {
  return Object.keys(TIMEZONE_CONFIG).map((tz) => ({
    value: tz,
    label: tz,
  }));
};
